import React, { memo, useState } from 'react'
import { Box } from '@alobato/flex-box'
import useFetch from '@alobato/use-fetch'
import CircularProgress from '@alobato/circular-progress'

import Card from '../../components/Card'
import ReactFileBase64 from '../../components/ReactFileBase64'
import {
  EmptyHint, FieldError, FormLabel, FormSection, GhostButton, PageHeader, PrimaryButton, tokens
} from '../../components/ui'

import AUTH_TOKEN from '../../constants/authToken'
import BASE_API from '../../constants/baseApi'

/* Configurações — os documentos legais.

   Termo de uso e política de privacidade eram trocáveis só pelo Django admin,
   que não é onde a equipe trabalha. Por isso o que está publicado hoje em
   produção é um `pdf_sample_2.pdf` com "PDF de Exemplo" repetido, ocupando o
   lugar do termo que o paciente aceita ao se cadastrar.

   Um termo e uma política para todos os perfis: a tela do médico mostra o
   mesmo arquivo do paciente. Foi decisão de produto.

   O upload é um de cada vez, e de propósito: campo não enviado não mexe no
   documento atual. Quem sobe só a política nova não corre o risco de apagar o
   termo sem perceber. */

const CONFIG_URL = `${BASE_API}/config`

const DOCUMENTOS = [
  {
    campo: 'terms_of_use',
    titulo: 'Termos de Uso',
    ajuda: 'Aceito pelo paciente no cadastro. Também é o documento que o médico vê no perfil dele.'
  },
  {
    campo: 'privacy_policy',
    titulo: 'Política de Privacidade',
    ajuda: 'Como os dados de saúde são tratados. Aparece no perfil do paciente.'
  }
]

const Documento = ({ doc, urlAtual, onEnviar, enviando }) => {
  const [novo, setNovo] = useState(null)
  const [erro, setErro] = useState('')

  const escolher = arquivos => {
    setErro('')
    if (!arquivos || arquivos.length === 0) return

    const arquivo = arquivos[0]
    const nome = (arquivo.name || '').toLowerCase()

    /* O servidor recusa o que não for PDF, mas avisar aqui evita a viagem —
       e a mensagem chega antes de a pessoa clicar em publicar. */
    if (!nome.endsWith('.pdf')) {
      setErro('O arquivo precisa ser um PDF.')
      return
    }
    setNovo(arquivo)
  }

  const publicar = async () => {
    if (!novo) return
    const ok = await onEnviar(doc.campo, novo.base64)
    if (ok) setNovo(null)
  }

  return (
    <Box mb={4}>
      <FormSection>{doc.titulo}</FormSection>

      <Box mb={2} css={{ fontSize: 13, color: tokens.textSoft }}>{doc.ajuda}</Box>

      <Box mb={2}>
        <FormLabel>Publicado atualmente</FormLabel>
        {urlAtual ? (
          <a
            href={urlAtual}
            target='_blank'
            rel='noopener noreferrer'
            style={{ color: tokens.brand, fontWeight: 600, fontSize: 14 }}
          >
            Abrir o documento publicado
          </a>
        ) : (
          <EmptyHint>Nenhum documento publicado.</EmptyHint>
        )}
      </Box>

      <Box mb={2}>
        <FormLabel>Substituir por</FormLabel>
        <ReactFileBase64 multiple={false} onDone={escolher} />
        {novo && (
          <Box mt={2} css={{ fontSize: 13, color: tokens.text }}>
            Selecionado: <strong>{novo.name}</strong>
            <Box d='inline-block' ml={2}>
              <GhostButton type='button' onClick={() => setNovo(null)}>Cancelar</GhostButton>
            </Box>
          </Box>
        )}
        <FieldError>{erro}</FieldError>
      </Box>

      <PrimaryButton type='button' onClick={publicar} disabled={!novo || enviando}>
        {enviando ? 'Publicando...' : `Publicar ${doc.titulo.toLowerCase()}`}
      </PrimaryButton>
    </Box>
  )
}

const Home = memo(({ data, notifier }) => {
  const [config, setConfig] = useState(data)
  const [enviando, setEnviando] = useState(false)

  const enviar = async (campo, base64) => {
    setEnviando(true)
    try {
      const body = new FormData()
      body.append(campo, base64)

      const resposta = await fetch(CONFIG_URL, {
        method: 'PUT',
        body,
        headers: { Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}` }
      })
      const retorno = await resposta.json().catch(() => null)

      if (!resposta.ok) {
        notifier.error((retorno && (retorno.detail || retorno.message)) || 'Não foi possível publicar.')
        return false
      }

      setConfig(retorno)
      notifier.success('Documento publicado!')
      return true
    } catch (e) {
      console.error(e)
      notifier.error('Falha de conexão. Tente de novo.')
      return false
    } finally {
      setEnviando(false)
    }
  }

  /* O `Box p={[0, 4]}` e o `Card p={[3, 4]}` são o envelope que todas as
     outras telas do admin usam (Planos, Pacientes, Médicos…). Sem eles esta
     tela ficava colada na barra lateral e sem margem interna nenhuma —
     destoava de todas as outras, e só dá para ver depois de publicada. */
  return (
    <Box p={[0, 4]} mb={5}>
      <Card p={[3, 4]}>
        <PageHeader title='Configurações' />

        <Box mb={4} css={{ fontSize: 14, color: tokens.textSoft, lineHeight: 1.6 }}>
          Os documentos publicados aqui aparecem imediatamente no portal e no aplicativo —
          não é preciso publicar uma nova versão do site.
        </Box>

        {DOCUMENTOS.map(doc => (
          <Documento
            key={doc.campo}
            doc={doc}
            urlAtual={config ? config[doc.campo] : null}
            onEnviar={enviar}
            enviando={enviando}
          />
        ))}
      </Card>
    </Box>
  )
})

export default memo(props => {
  const { data, error, loading } = useFetch(
    CONFIG_URL,
    { headers: { Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}` } }
  )
  if (error) return `Erro! ${error.message}`
  if (!data || loading) return <CircularProgress color='hsla(199, 75%, 43%, 1)' />
  return <Home {...props} data={data} />
})
